import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { FiPlus, FiTrash2, FiUser, FiMail, FiCalendar, FiX, FiUsers, FiEdit2, FiSave, FiLogIn } from 'react-icons/fi';
import LoadingButton, { ButtonSpinner } from '../components/LoadingButton';
import IconButton from '../components/IconButton';
import MoreMenu from '../components/MoreMenu';
import Modal from '../components/Modal';
import UserActivityTimeline from '../components/UserActivityTimeline';
import SendTemplateEmailModal from '../components/SendTemplateEmailModal';
import { useListViewPreferences } from '../hooks/useListViewPreferences';
import ListColumnPicker from '../components/ListColumnPicker';
import ListSearchBar from '../components/ListSearchBar';
import ListPager from '../components/ListPager';
import RoleSelect from '../components/RoleSelect';
import { useAppRoles, roleLabel, roleAllowsLogin } from '../hooks/useAppRoles';
import { LIST_VIEW_KEYS, USER_COLUMNS } from '../config/listViewColumns';
import './Users.css';
import { formatRecordCreatedOn, formatRecordModifiedOn } from '../utils/formatTimestamp';
import { appAlert, appConfirm } from '../utils/appDialog';
import { useClientPager } from '../hooks/useClientPager';

const ROLE_FILTERS = [
  { value: 'staff', label: 'Staff' },
  { value: 'external', label: 'External' },
  { value: 'all', label: 'All users' },
  { value: 'ticket_logger', label: 'Ticket loggers' },
  { value: 'consultant', label: 'Consultants' },
  { value: 'candidate', label: 'Candidates' },
  { value: 'admin', label: 'Admins' },
];

const EXTERNAL_ROLES = new Set(['external', 'external_ticket_logger']);
const TICKET_LOGGER_ROLES = new Set(['ticket_logger', 'external_ticket_logger']);

const isStaffUser = (u) => !EXTERNAL_ROLES.has(u.role) && u.role !== 'candidate';
const isExternalUser = (u) => EXTERNAL_ROLES.has(u.role);
const isTicketLogger = (u) => TICKET_LOGGER_ROLES.has(u.role);

const Users = () => {
  const { user, impersonate, isImpersonating } = useAuth();
  const { data: appRoles = [] } = useAppRoles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [roleFilter, setRoleFilter] = useState('staff');
  const [userSearch, setUserSearch] = useState('');
  const appliedSavedRoleFilter = useRef(false);
  const [detailTab, setDetailTab] = useState('profile');
  const [showModal, setShowModal] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'candidate',
    phone: '',
    is_active: true,
  });

  const formAllowsLogin = roleAllowsLogin(formData.role, appRoles);

  const {
    colIndex,
    toggleColumn,
    moveColumn,
    resetColumns,
    visibleColumnIds,
    allColumns,
    savedView,
    persistView,
    getColumnProps,
    ResizeHandle,
    tableRef,
  } = useListViewPreferences(LIST_VIEW_KEYS.USERS, USER_COLUMNS);

  const { data: users, isLoading } = useQuery(
    'users',
    () => api.get('/users').then(res => res.data),
    { enabled: user?.role === 'admin' && Boolean(user?.id) }
  );

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    let list =
      roleFilter === 'staff'
        ? users.filter(isStaffUser)
        : roleFilter === 'external'
          ? users.filter(isExternalUser)
          : roleFilter === 'ticket_logger'
            ? users.filter(isTicketLogger)
            : roleFilter === 'all'
              ? users
              : users.filter((u) => u.role === roleFilter);
    const q = userSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((u) => {
      const hay = [u.first_name, u.last_name, u.email, u.phone, u.role]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [users, roleFilter, userSearch]);

  const {
    pageItems: pagedUsers,
    pagination: usersPagination,
    setPage: setUsersPage,
  } = useClientPager(filteredUsers, { resetKey: `${roleFilter}|${userSearch}` });

  useEffect(() => {
    if (!appliedSavedRoleFilter.current && savedView.roleFilter) {
      setRoleFilter(savedView.roleFilter);
      appliedSavedRoleFilter.current = true;
    }
  }, [savedView.roleFilter]);

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
    persistView({ roleFilter: value });
  };

  // Fetch detailed user info when a user is selected
  const { data: userDetails, isLoading: loadingDetails } = useQuery(
    ['user-details', selectedUser?.id],
    () => api.get(`/users/${selectedUser.id}`).then(res => res.data),
    {
      enabled: !!selectedUser && showUserDetails,
    }
  );

  const createMutation = useMutation(
    (data) => api.post('/users', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        setShowModal(false);
        setFormData({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'candidate',
          phone: '',
          is_active: true,
        });
      },
      onError: (err) => {
        const msg = err.response?.data?.error || err.message;
        void appAlert(msg || 'Could not create user');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/users/${id}`, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries(['user-details', variables.id]);
        setShowModal(false);
        setEditingUser(null);
        setIsEditing(false);
        setFormData({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'candidate',
          phone: '',
          is_active: true,
        });
      },
    }
  );

  const updatePasswordMutation = useMutation(
    ({ id, password }) => api.put(`/users/${id}/password`, { password }),
    {
      onSuccess: (data, variables) => {
        setFormData({ ...formData, password: '' });
        queryClient.invalidateQueries(['user-details', variables.id]);
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/users/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        setShowUserDetails(false);
        setSelectedUser(null);
        setIsEditing(false);
      },
      onError: (err) => {
        void appAlert(err.response?.data?.error || err.message || 'Failed to delete user');
      },
    }
  );

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (selectedUser.id === user?.id) {
      void appAlert('Cannot delete your own account');
      return;
    }
    if (await appConfirm(`Are you sure you want to delete ${selectedUser.first_name} ${selectedUser.last_name}?`)) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  const canLoginAsUser = (target) =>
    user?.role === 'admin' &&
    !isImpersonating &&
    target &&
    target.id !== user?.id &&
    target.role !== 'admin' &&
    roleAllowsLogin(target.role, appRoles);

  const handleLoginAs = async (target) => {
    if (!canLoginAsUser(target)) return;
    const name = [target.first_name, target.last_name].filter(Boolean).join(' ').trim() || target.email;
    if (!await appConfirm(`Login as ${name}? You will see the app as this user until you return to admin.`)) {
      return;
    }
    setImpersonatingUserId(target.id);
    try {
      await impersonate(target.id);
      queryClient.clear();
      setShowUserDetails(false);
      setSelectedUser(null);
      navigate('/');
    } catch (err) {
      console.error('Impersonation failed:', err);
      void appAlert(err?.response?.data?.error || 'Failed to login as user');
    } finally {
      setImpersonatingUserId(null);
    }
  };

  const handleUserClick = (row) => {
    const roleMeta = appRoles.find((r) => r.slug === row.role);
    if (roleMeta?.grants_candidate_profile || row.role === 'candidate') {
      navigate(`/candidates/${row.id}`);
      return;
    }
    setSelectedUser(row);
    setShowUserDetails(true);
    setIsEditing(false);
    setDetailTab('profile');
  };

  const handleEditUser = () => {
    if (userDetails) {
      setFormData({
        email: userDetails.email,
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        role: userDetails.role,
        phone: userDetails.phone || '',
        is_active: userDetails.is_active,
        password: '',
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'candidate',
      phone: '',
      is_active: true,
    });
  };

  const handleSaveUser = (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    const submitData = { ...formData };
    const { password, ...userData } = submitData;
    
    // Update user info
    updateMutation.mutate({ id: selectedUser.id, data: userData });
    
    // Update password separately if provided (not for external / no-login roles)
    if (password && password.trim() !== '' && roleAllowsLogin(formData.role, appRoles)) {
      updatePasswordMutation.mutate({ id: selectedUser.id, password });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (editingUser) {
      if (!submitData.password || String(submitData.password).trim() === '') {
        delete submitData.password;
      }
      updateMutation.mutate({ id: editingUser.id, data: submitData });
    } else {
      if (!roleAllowsLogin(submitData.role, appRoles)) {
        delete submitData.password;
      }
      createMutation.mutate(submitData);
    }
  };

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (isLoading && users == null) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="users-page list-page">
      <div className="page-header">
        <h1>Users</h1>
        <div className="list-page-header-actions page-toolbar">
          <ListColumnPicker
            allColumns={allColumns}
            visibleColumnIds={visibleColumnIds}
            onToggle={toggleColumn}
            onMove={moveColumn}
            onReset={resetColumns}
          />
          <IconButton
            icon={FiPlus}
            label="Add user"
            variant="primary"
            onClick={() => {
              setEditingUser(null);
              setFormData({
                email: '',
                password: '',
                first_name: '',
                last_name: '',
                role: 'candidate',
                phone: '',
                is_active: true,
              });
              setShowModal(true);
            }}
          />
        </div>
      </div>

      <div className="list-filters-panel list-toolbar-panel">
        <div className="filter-row list-toolbar-row">
          <div className="filter-group filter-group--wide list-toolbar-search">
            <ListSearchBar
              id="users-search"
              value={userSearch}
              onChange={setUserSearch}
              placeholder="Search name, email, phone…"
            />
          </div>
        </div>
      </div>

      <div className="users-role-filters">
        {ROLE_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            className={`users-role-filter${roleFilter === value ? ' active' : ''}`}
            onClick={() => handleRoleFilterChange(value)}
          >
            {label}
            {users && (
              <span className="users-role-filter-count">
                {value === 'all'
                  ? users.length
                  : value === 'staff'
                    ? users.filter(isStaffUser).length
                    : value === 'external'
                      ? users.filter(isExternalUser).length
                      : value === 'ticket_logger'
                        ? users.filter(isTicketLogger).length
                        : users.filter((u) => u.role === value).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="users-table-container">
        <table ref={tableRef} className="table users-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              {visibleColumnIds.map((columnId) => {
                const idx = colIndex(columnId);
                const labels = {
                  name: 'Name',
                  email: 'Email',
                  role: 'Role',
                  phone: 'Phone',
                  status: 'Status',
                };
                return (
                  <th key={columnId} {...getColumnProps(idx)}>
                    {labels[columnId] || columnId}
                    <ResizeHandle index={idx} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pagedUsers.length > 0 ? (
              pagedUsers.map((row) => (
                <tr
                  key={row.id}
                  className="user-row"
                  onClick={() => handleUserClick(row)}
                >
                  {visibleColumnIds.map((columnId) => {
                    if (columnId === 'name') {
                      return (
                        <td key={columnId}>
                          <div className="user-name-cell">
                            <FiUser className="user-icon" />
                            <span>{row.first_name} {row.last_name}</span>
                            {canLoginAsUser(row) && (
                              <MoreMenu
                                label={`Actions for ${row.first_name} ${row.last_name}`}
                                size="sm"
                                items={[
                                  {
                                    id: 'login-as',
                                    label:
                                      impersonatingUserId === row.id
                                        ? 'Logging in…'
                                        : 'Login as',
                                    icon: FiLogIn,
                                    disabled: impersonatingUserId === row.id,
                                    onSelect: () => handleLoginAs(row),
                                  },
                                ]}
                              />
                            )}
                          </div>
                        </td>
                      );
                    }
                    if (columnId === 'email') {
                      return <td key={columnId}>{row.email}</td>;
                    }
                    if (columnId === 'role') {
                      return (
                        <td key={columnId}>
                          <span className={`badge badge-info badge-role-${row.role}`}>
                            {roleLabel(row.role, appRoles)}
                          </span>
                        </td>
                      );
                    }
                    if (columnId === 'phone') {
                      return <td key={columnId}>{row.phone || '—'}</td>;
                    }
                    if (columnId === 'status') {
                      return (
                        <td key={columnId}>
                          <span className={`badge badge-${row.is_active ? 'success' : 'danger'}`}>
                            {row.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      );
                    }
                    return null;
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(visibleColumnIds.length, 1)} className="empty-state">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ListPager
        page={usersPagination.page}
        pageCount={usersPagination.pageCount}
        total={usersPagination.total}
        limit={usersPagination.limit}
        onPageChange={setUsersPage}
      />

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        ariaLabel={editingUser ? 'Edit user' : 'Add user'}
      >
        <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>
        <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              {(!editingUser || formData.password) && formAllowsLogin && (
                <div className="form-group">
                  <label>Password{editingUser && ' (leave blank to keep current)'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
              )}
              {!formAllowsLogin && !editingUser && (
                <p className="form-hint" style={{ marginTop: 0, marginBottom: '0.75rem', color: 'var(--text-muted, #666)', fontSize: '0.9rem' }}>
                  External contacts cannot sign in. Their email is used for approvals, invoices, and other outbound notices.
                </p>
              )}
              <div className="form-group">
                <label>Role</label>
                <RoleSelect
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value, password: roleAllowsLogin(e.target.value, appRoles) ? formData.password : '' })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
      </Modal>

      <Modal
        open={Boolean(showUserDetails && selectedUser)}
        onClose={() => {
          setShowUserDetails(false);
          setSelectedUser(null);
          setIsEditing(false);
        }}
        contentClassName="user-details-modal"
        ariaLabel="User details"
      >
            <div className="modal-header">
              <h2>
                <FiUser aria-hidden /> {selectedUser?.first_name} {selectedUser?.last_name}
              </h2>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => {
                  setShowUserDetails(false);
                  setSelectedUser(null);
                  setIsEditing(false);
                }}
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>

            {selectedUser && !isEditing && (
              <div className="user-detail-tabs">
                <button
                  type="button"
                  className={`user-detail-tab${detailTab === 'profile' ? ' active' : ''}`}
                  onClick={() => setDetailTab('profile')}
                >
                  Profile
                </button>
                {(selectedUser.role === 'consultant' || selectedUser.role === 'candidate') && (
                  <button
                    type="button"
                    className={`user-detail-tab${detailTab === 'activity' ? ' active' : ''}`}
                    onClick={() => setDetailTab('activity')}
                  >
                    Activity &amp; CRM
                  </button>
                )}
              </div>
            )}

            {loadingDetails ? (
              <div className="loading">Loading user details...</div>
            ) : userDetails ? (
              <>
                {detailTab === 'activity' && !isEditing && (selectedUser.role === 'consultant' || selectedUser.role === 'candidate') ? (
                  <UserActivityTimeline userId={selectedUser.id} userRole={selectedUser.role} />
                ) : !isEditing ? (
                  // View Mode
                  <div className="user-details-view">
                    <div className="user-profile-header">
                      <div className="user-avatar">
                        <FiUser size={48} />
                      </div>
                      <div className="user-basic-info">
                        <h3>{userDetails.first_name} {userDetails.last_name}</h3>
                        <p className="user-email">
                          <FiMail /> {userDetails.email}
                        </p>
                        <div className="user-status-badges">
                          <span className={`badge badge-${userDetails.is_active ? 'success' : 'danger'}`}>
                            {userDetails.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="badge badge-info">{userDetails.role}</span>
                        </div>
                      </div>
                    </div>

                    <div className="user-details-sections">
                      <div className="detail-section">
                        <h4>
                          <FiUser /> Personal Information
                        </h4>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <strong>First Name:</strong>
                            <span>{userDetails.first_name}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Last Name:</strong>
                            <span>{userDetails.last_name}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Email:</strong>
                            <span>{userDetails.email}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Phone:</strong>
                            <span>{userDetails.phone || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Role:</strong>
                            <span className="badge badge-info">{userDetails.role}</span>
                          </div>
                          <div className="detail-item">
                            <strong>Status:</strong>
                            <span className={`badge badge-${userDetails.is_active ? 'success' : 'danger'}`}>
                              {userDetails.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>
                          <FiCalendar /> Account Information
                        </h4>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <strong>Created:</strong>
                            <span>{formatRecordCreatedOn(userDetails)}</span>
                          </div>
                          {(userDetails.modifiedon_timestamp || userDetails.updated_at) && (
                            <div className="detail-item">
                              <strong>Last Updated:</strong>
                              <span>{formatRecordModifiedOn(userDetails)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {userDetails.groups && userDetails.groups.length > 0 && (
                        <div className="detail-section">
                          <h4>
                            <FiUsers /> Groups ({userDetails.groups.length})
                          </h4>
                          <div className="groups-list-inline">
                            {userDetails.groups.map((group) => (
                              <span key={group.id} className="group-badge">
                                {group.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="modal-actions user-detail-modal-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleEditUser}
                      >
                        <FiEdit2 /> Edit User
                      </button>
                      {userDetails.email && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowSendEmail(true)}
                          title="Send a canned email template"
                        >
                          <FiMail /> Send email
                        </button>
                      )}
                      {canLoginAsUser(selectedUser) && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleLoginAs(selectedUser)}
                          disabled={impersonatingUserId === selectedUser.id}
                          title="Login as this user"
                        >
                          {impersonatingUserId === selectedUser.id ? <ButtonSpinner /> : <FiLogIn />}
                          {impersonatingUserId === selectedUser.id ? 'Logging in…' : 'Login as'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowUserDetails(false);
                          setSelectedUser(null);
                        }}
                      >
                        Close
                      </button>
                      {selectedUser.id !== user?.id && (
                        <button
                          type="button"
                          className="btn btn-danger user-detail-delete-btn"
                          onClick={handleDeleteUser}
                          disabled={deleteMutation.isLoading}
                          title="Delete user"
                        >
                          {deleteMutation.isLoading ? <ButtonSpinner /> : <FiTrash2 />}
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSaveUser} className="user-edit-form">
                    <div className="user-profile-header">
                      <div className="user-avatar">
                        <FiUser size={48} />
                      </div>
                      <div className="user-basic-info">
                        <h3>Edit User Profile</h3>
                        <p className="user-email">{userDetails.email}</p>
                      </div>
                    </div>

                    <div className="form-sections">
                      <div className="form-section">
                        <h4>Personal Information</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>First Name</label>
                            <input
                              type="text"
                              value={formData.first_name}
                              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Last Name</label>
                            <input
                              type="text"
                              value={formData.last_name}
                              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Phone</label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="form-section">
                        <h4>Account Settings</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Role</label>
                            <RoleSelect
                              value={formData.role}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  role: e.target.value,
                                  password: roleAllowsLogin(e.target.value, appRoles) ? formData.password : '',
                                })
                              }
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Status</label>
                            <select
                              value={formData.is_active ? 'active' : 'inactive'}
                              onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                        {formAllowsLogin ? (
                          <div className="form-group">
                            <label>New Password (leave blank to keep current)</label>
                            <input
                              type="password"
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                              placeholder="Enter new password"
                            />
                          </div>
                        ) : (
                          <p className="form-hint" style={{ color: 'var(--text-muted, #666)', fontSize: '0.9rem' }}>
                            This role cannot sign in to the app. Email is used for approvals and outbound notices only.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                      <LoadingButton
                        type="submit"
                        className="btn btn-primary"
                        icon={FiSave}
                        loading={updateMutation.isLoading}
                        loadingLabel="Saving..."
                      >
                        Save Changes
                      </LoadingButton>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <div className="error">Failed to load user details</div>
            )}
      </Modal>

      <SendTemplateEmailModal
        open={Boolean(showSendEmail && (userDetails || selectedUser))}
        onClose={() => setShowSendEmail(false)}
        recipient={
          userDetails || selectedUser
            ? {
                userId: (userDetails || selectedUser).id,
                email: (userDetails || selectedUser).email,
                firstName: (userDetails || selectedUser).first_name,
                lastName: (userDetails || selectedUser).last_name,
                displayName: `${(userDetails || selectedUser).first_name || ''} ${(userDetails || selectedUser).last_name || ''}`.trim(),
              }
            : null
        }
      />
    </div>
  );
};

export default Users;

